import { Fragment, useCallback, useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import type { CreateExternalDocumentRequest, ExternalDocCategory, ExternalDocStatus, ExternalDocument } from "../../../models/eqms";
import {
  createExternalDocument,
  deleteExternalDocument,
  downloadExternalDocRevision,
  fetchExternalDocRevisionBytes,
  listExternalDocRevisions,
  listExternalDocuments,
  uploadExternalDocFile,
} from "../../../controllers/eqmsApi";
import { friendlyMessage } from "../../../controllers/projectApi";
import { ExternalDocRevisions } from "./ExternalDocRevisions";
import { DocPreviewModal } from "../../components/DocPreviewModal";
import { FileDrop } from "../../components/FileDrop";

const CATEGORY_LABEL: Record<ExternalDocCategory, string> = {
  STANDARD: "표준",
  REGULATION: "법규",
  GUIDANCE: "가이던스",
  SUPPLIER: "공급업체",
  OTHER: "기타",
};
const CATEGORIES: ExternalDocCategory[] = ["STANDARD", "REGULATION", "GUIDANCE", "SUPPLIER", "OTHER"];
const STATUS_LABEL: Record<ExternalDocStatus, string> = {
  ACTIVE: "유효",
  SUPERSEDED: "대체됨",
  WITHDRAWN: "철회",
};

// 외부문서: 규격/법규 카탈로그(워크스페이스 단위). 목록 + 생성 + 삭제.
export function EqmsExternalView({ eqms }: { eqms: EqmsController }) {
  const workspaceId = eqms.workspaceId;
  const [docs, setDocs] = useState<ExternalDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /** Which row has its file panel open — one at a time keeps the table readable. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<ExternalDocument | null>(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      setDocs(await listExternalDocuments(eqms.getToken, workspaceId));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [eqms.getToken, workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function addDoc(req: CreateExternalDocumentRequest, file: File | null) {
    if (!workspaceId) return;
    setError(null);
    try {
      const created = await createExternalDocument(eqms.getToken, workspaceId, req);
      // Creating and attaching are two calls, but one action to the user: if the upload fails the
      // catalogue entry still exists, so say so rather than leaving them guessing.
      if (file) {
        await uploadExternalDocFile(eqms.getToken, created.id, file, "최초 등록");
      }
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await deleteExternalDocument(eqms.getToken, id);
      setDocs((cur) => cur.filter((d) => d.id !== id));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  if (!workspaceId) {
    return <div className="note">워크스페이스를 먼저 선택하세요.</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>외부문서 · 규격/법규</h3>
        <button className="btn primary" style={{ width: "auto" }} onClick={() => setCreating((c) => !c)}>
          {creating ? "취소" : "＋ 외부문서"}
        </button>
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {creating && (
        <CreateExternalDocForm
          onSubmit={async (req, file) => {
            await addDoc(req, file);
            setCreating(false);
          }}
        />
      )}

      {/* Only blank the screen on the FIRST load. A background refresh (after an upload) used to
          unmount the whole table, which took the expanded file panel — and the diff it had just
          opened — down with it, and flashed the page for every save. */}
      {loading && docs.length === 0 ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <table className="sdui-table">
          <thead>
            <tr>
              <th>참조번호</th>
              <th>제목</th>
              <th>구분</th>
              <th>발행기관</th>
              <th>판</th>
              <th>상태</th>
              <th>시행일</th>
              <th>검토일</th>
              <th>파일</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <Fragment key={d.id}>
                <tr>
                  <td className="mono">{d.externalRef || "-"}</td>
                  <td>{d.title}</td>
                  <td>
                    <span className="tag">{CATEGORY_LABEL[d.category]}</span>
                  </td>
                  <td>{d.issuer || "-"}</td>
                  <td>{d.edition || "-"}</td>
                  <td>{STATUS_LABEL[d.status]}</td>
                  <td className="mono">{d.effectiveDate || "-"}</td>
                  <td className="mono">{d.reviewDate || "-"}</td>
                  <td>
                    {/* 목록에서도 파일명을 누르면 최신 판이 바로 열린다 — 파일 관리를 펼칠 필요 없이. */}
                    {d.filename
                      ? <button className="filename-link" onClick={() => setPreviewing(d)}>{d.filename}</button>
                      : <span className="sdui-help">없음</span>}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button
                        className="sdui-button sdui-button-secondary"
                        onClick={() => setOpenId((cur) => (cur === d.id ? null : d.id))}
                      >
                        {openId === d.id ? "파일 닫기" : "파일 관리"}
                      </button>
                      <button className="sdui-button sdui-button-danger" onClick={() => void remove(d.id)}>
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
                {openId === d.id && (
                  <tr>
                    <td colSpan={10} style={{ background: "var(--bg)" }}>
                      <ExternalDocRevisions getToken={eqms.getToken} documentId={d.id} onUploaded={() => void reload()} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
          {docs.length === 0 && (
            <tfoot>
              <tr>
                <td colSpan={10} className="sdui-table-empty">
                  외부문서가 없습니다.
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      )}

      {previewing && (
        <DocPreviewModal
          filename={previewing.filename || previewing.title}
          subtitle={previewing.title}
          /* 목록은 어느 판이 최신인지 모르므로 열 때 리비전을 조회해 가장 최근 것을 읽는다. */
          load={() => latestRevision(eqms.getToken, previewing.id)
            .then((rev) => fetchExternalDocRevisionBytes(eqms.getToken, previewing.id, rev.id))}
          onDownload={() => void latestRevision(eqms.getToken, previewing.id)
            .then((rev) => downloadExternalDocRevision(
              eqms.getToken, previewing.id, rev.id, previewing.filename || previewing.title))
            .catch((e) => setError(friendlyMessage(e)))}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}

/** 최신 판(리비전 번호가 가장 큰 것). 목록 응답에는 리비전 id가 없어 열 때 한 번 조회한다. */
async function latestRevision(getToken: () => string | null, documentId: string) {
  const revisions = await listExternalDocRevisions(getToken, documentId);
  const latest = revisions.reduce<(typeof revisions)[number] | null>(
    (best, r) => (!best || r.revNo > best.revNo ? r : best), null);
  if (!latest) throw new Error("올라온 파일이 없습니다.");
  return latest;
}

function CreateExternalDocForm({
  onSubmit,
}: {
  onSubmit: (req: CreateExternalDocumentRequest, file: File | null) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [externalRef, setExternalRef] = useState("");
  const [category, setCategory] = useState<ExternalDocCategory>("STANDARD");
  const [issuer, setIssuer] = useState("");
  const [edition, setEdition] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await onSubmit({
        title,
        externalRef: externalRef || undefined,
        category,
        issuer: issuer || undefined,
        edition: edition || undefined,
        effectiveDate: effectiveDate || undefined,
        reviewDate: reviewDate || undefined,
      }, file);
      setTitle("");
      setExternalRef("");
      setIssuer("");
      setEdition("");
      setEffectiveDate("");
      setReviewDate("");
      setFile(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="info-card" style={{ marginBottom: 16 }}>
      <h4>새 외부문서</h4>
      <div className="extdoc-form">
        <label className="sdui-field span2">
          <span className="sdui-label">제목</span>
          <input className="sdui-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="sdui-field">
          <span className="sdui-label">참조번호</span>
          <input className="sdui-input" value={externalRef} onChange={(e) => setExternalRef(e.target.value)} />
        </label>
        <label className="sdui-field">
          <span className="sdui-label">구분</span>
          <select className="sdui-input" value={category} onChange={(e) => setCategory(e.target.value as ExternalDocCategory)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </label>
        <label className="sdui-field">
          <span className="sdui-label">발행기관</span>
          <input className="sdui-input" value={issuer} onChange={(e) => setIssuer(e.target.value)} />
        </label>
        <label className="sdui-field">
          <span className="sdui-label">판</span>
          <input className="sdui-input" value={edition} onChange={(e) => setEdition(e.target.value)} />
        </label>
        <label className="sdui-field">
          <span className="sdui-label">시행일</span>
          <input type="date" className="sdui-input" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
        </label>
        <label className="sdui-field">
          <span className="sdui-label">검토일</span>
          <input type="date" className="sdui-input" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} />
        </label>
        {/* Its own full-width row: a drop target needs room, and inlining it is what threw the
            other fields' baselines off. */}
        <label className="sdui-field span-all">
          <span className="sdui-label">파일 (선택)</span>
          <FileDrop
            file={file}
            onFile={setFile}
            compact
            hint='지금 올리지 않아도 됩니다 — 만든 뒤 "파일 관리"에서 올리거나 새 버전으로 교체할 수 있습니다.'
          />
        </label>
      </div>
      <div className="extdoc-form-foot">
        <button className="btn primary" style={{ width: "auto" }} disabled={!title.trim() || busy} onClick={submit}>
          {busy ? "생성 중…" : "생성"}
        </button>
      </div>
    </div>
  );
}
